import RBACFlag from "@/app/components/core/RBACFlag";
import { ICityOnboardFormFields } from "@/app/components/routes/auth_routes/Configuration/CitySetup/CityOnboard";
import { ImageValidationResult } from "@/app/components/routes/auth_routes/Configuration/ClinicSetup/ClinicOnboard/interface";
import ShiftActiveDeactiveModalComponent from "@/app/components/routes/auth_routes/RotaManagement/Modals/ShiftActiveDeactiveModalComponent";
import ExpandableList from "@/app/components/ui/Reusable/ExpandableList";
import Toggle from "@/app/components/ui/Toggles";
import { startCase } from "lodash";
import React, { useEffect, useMemo } from "react";
import { BiEdit } from "react-icons/bi";

export const getCityColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Image",
    cell: (row: any) =>
      React.createElement("img", {
        width: 50,
        height: 50,
        src: row?.image_url,
        alt: "city",
      }),
  },
  {
    header: "City Name",
    accessor: "name",
  },
  {
    header: "Latitude",
    accessor: "lat",
  },
  {
    header: "Longitude",
    accessor: "long",
  },
  {
    header: "City Order",
    accessor: "city_order",
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "CITY_SETUP_ONBOARD_EDIT" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("CITY_SETUP_ONBOARD", "url")}?city_id=${row?.id}`);
            },
          },
          "Edit",
        ),
      ),
  },
];

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const validateCityImage = (file: File) => {
  return new Promise<{
    isValid: boolean;
    error?: string;
  }>((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target?.result as string;

      img.onload = () => {
        const isPng = file.type === "image/png";
        const isSizeValid = file.size < 20000;
        const isDimensionValid = img.width === 50 && img.height === 50;

        if (!isPng) return resolve({ isValid: false, error: "Only PNG allowed" });
        if (!isSizeValid) return resolve({ isValid: false, error: "Max size 20KB" });
        if (!isDimensionValid) return resolve({ isValid: false, error: "Size must be 50x50" });

        resolve({ isValid: true });
      };
    };

    reader.readAsDataURL(file);
  });
};

export const handleDecimalInput = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, "");
  return /^\d*\.?\d*$/.test(cleaned) ? cleaned : "";
};

export const getCityPayload = (data: ICityOnboardFormFields, image: string) => ({
  city_image: {
    image_base64: image?.split(",")[1] || image,
    image_extension: "png",
  },
  city_name: data.city,
  display_on_app: data.displayOnApp,
  display_on_vms: data.displayOnVms,
  latitude: data.latitude,
  longitude: data.longitude,
  pincode: data.pincode,
  state_name: data.state,
});

export const getEquipmentColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Name",
    accessor: "equipment_name",
  },
  {
    header: "Service Name",
    cell: (row: any) =>
      row.service_details?.map((service: any) =>
        React.createElement("div", { key: service.service_id }, service.service_name),
      ),
  },
  {
    header: "Sub Service Name",
    cell: (row: any) =>
      row.service_details?.map((service: any) =>
        service.sub_services?.map((sub: any) =>
          React.createElement("div", { key: sub.sub_service_id }, sub.sub_service_name),
        ),
      ),
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "EQUIPMENT_SETUP_ONBOARD_EDIT" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("EQUIPMENT_SETUP_ONBOARD", "url")}?equipment_id=${row?.equipment_id}`);
            },
          },
          "Edit",
        ),
      ),
  },
];

export const mapServicesToSelectItems = (orgServices: any[]) => {
  if (!Array.isArray(orgServices)) return [];
  return orgServices.map((service) => ({
    id: service?.service_id,
    title: service?.service_name,
    checkboxVisible: true,
    withSubItems: true,
    subSelectItems: (service?.sub_services || []).map((sub) => ({
      id: sub?.sub_service_id,
      title: sub?.sub_service_name,
    })),
  }));
};

export const getEmptyServices = (services) => {
  return services?.filter((s) => !s?.subSelectItems?.length)?.map((s) => s?.title);
};

export const validateServices = (services) => {
  if (!services?.length) {
    return { valid: false, message: "Please select a service and it's sub-service" };
  }
  const empty = getEmptyServices(services);
  if (empty?.length) {
    return {
      valid: false,
      message: `${empty.join(",")} sub-services should be selected.`,
    };
  }
  return { valid: true };
};

export const buildEquipmentPayload = (data, clinicDetails) => {
  const services_data = data?.services?.map((s) => ({
    service_id: s?.id,
    sub_service_ids: s?.subSelectItems?.map((ss) => ss?.id),
  }));
  const clinics = clinicDetails
    ?.filter((c) => c?.clinic_id)
    ?.map((c) => ({
      clinic_id: c?.clinic_id,
      equipment_count: `${c?.equipment_count}`,
    }));
  return clinics?.length
    ? {
        equipment_name: data?.equipmentName,
        services_data,
        clinic_equipment_details: clinics,
      }
    : {
        equipment_name: data?.equipmentName,
        services_data,
      };
};

export const getServiceValidationError = (services) => {
  const result = validateServices(services);
  return result.valid ? null : result.message;
};

export const updateClinicCount = (list, index, updater) => {
  return list.map((item, i) => (i === index ? { ...item, equipment_count: updater(item.equipment_count) } : item));
};

export const mapSelectedServices = (services, selectedData) => {
  return services
    ?.filter((s) => selectedData.some((ss) => ss.service_id == s.id))
    ?.map((s) => {
      const selected = selectedData.find((ss) => ss.service_id == s.id);
      return {
        ...s,
        subSelectItems: s.subSelectItems?.filter((c) => selected?.sub_services_data?.includes(c.id)) || [],
      };
    });
};

export const getStaffCategoryColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Category",
    accessor: "staff_category_name",
  },
  {
    header: "Working Duration",
    cell: (row: any) => `${row?.working_duration} hr`,
  },
  {
    header: "Gap Duration",
    cell: (row: any) => `${row?.gap_time} min`,
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "STAFF_CATEGORY_SETUP_ONBOARD_EDIT" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("STAFF_CATEGORY_SETUP_ONBOARD", "url")}?staff_category_id=${row?.id}`);
            },
          },
          "Edit",
        ),
      ),
  },
];

export const getRolesColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Role",
    accessor: "role_name",
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "ROLES_PERMISSION_ONBOARD_EDIT" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("ROLES_PERMISSION_ONBOARD", "url")}?role_id=${row?.role_id}`);
            },
          },
          "Edit",
        ),
      ),
  },
];

export const getClinicColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_row: any, index: number) => index + 1,
  },
  {
    header: "Clinic Name",
    accessor: "display_name",
  },
  {
    header: "City",
    accessor: "city",
  },
  {
    header: "Contact Number",
    accessor: "mobile",
  },
  {
    header: "Visible On App",
    cell: (row: any) =>
      React.createElement(Toggle, {
        checked: Boolean(row?.display_on_app),
        disabled: true,
        onToggle: () => {},
      }),
  },
  {
    header: "Visible On VMS",
    cell: (row: any) =>
      React.createElement(Toggle, {
        checked: Boolean(row?.display_on_vms),
        disabled: true,
        onToggle: () => {},
      }),
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "CLINIC_SETUP_IMAGE_UPDATE" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900 inline-flex items-center gap-1",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("CLINIC_SETUP_IMAGE_UPDATE", "url")}/${row?.id}`);
            },
          },
          React.createElement(BiEdit, { className: "w-4 h-4" }),
          "Edit Image",
        ),
      ),
  },
];

export const useNormalizedServiceTypes = (equipmentService: any) => {
  return useMemo(() => {
    const serviceIdToServiceTypeIdMap: Record<string, string> = {};
    const grouped: Record<string, any> = {};
    const add = (id: string, name: string, subs: any[] = []) => {
      if (!id) return;
      if (!grouped[id]) {
        grouped[id] = {
          title: name,
          childrenMap: new Map(),
        };
      }
      subs.forEach((s) => {
        if (s?.sub_service_id) {
          grouped[id].childrenMap.set(s.sub_service_id, s.sub_service_name);
        }
      });
    };
    if (Array.isArray(equipmentService)) {
      equipmentService.forEach((s: any) => {
        const typeId = s.service_type_id || s.service_id;
        const typeName = s.service_type_name || s.service_name;
        serviceIdToServiceTypeIdMap[s.service_id] = typeId;
        add(typeId, typeName, s.sub_services ?? []);
      });
    } else {
      Object.values(equipmentService?.service_types ?? {}).forEach((type: any) => {
        (type.services ?? []).forEach((s: any) => {
          serviceIdToServiceTypeIdMap[s.service_id] = type.id;
          add(type.id, type.name, s.sub_services ?? []);
        });
      });
    }
    const options = Object.entries(grouped).map(([id, val]: any) => ({
      id,
      title: val.title,
      children: Array.from(val.childrenMap.entries()).map(([id, title]) => ({
        id,
        title,
      })),
    }));
    return { options, serviceIdToServiceTypeIdMap };
  }, [equipmentService]);
};

export const buildClinicImagesPayload = (formHook: any, listcategories: any, detailcategories: any) => {
  const buildPage = (formPage: any, categories: any) => {
    const imgs = {};
    for (const category in formPage) {
      if (formPage[category]?.length > 0)
        imgs[category] = {
          base_64: formPage[category]?.map((s: string) => s?.split(",")[1] || s),
          image_extension: "png",
        };
    }
    for (const category in categories) {
      if (categories[category]?.length > 0)
        imgs[category] = {
          base_64: categories[category]?.map((s: string) => (s?.slice(-3) == "png" ? s : s?.split(",")[1] || s)),
          image_extension: "png",
        };
    }
    return imgs;
  };
  return {
    image_version: {
      image_base64: formHook.watch("image")?.split(",")[1]
        ? formHook.watch("image")?.split(",")[1]
        : formHook.watch("image") || "",
      image_extension: "png",
    },
    clinic_listing_page: buildPage(formHook.watch("clinic_listing_page"), listcategories),
    clinic_details_page: buildPage(formHook.watch("clinic_details_page"), detailcategories),
  };
};

export const useImageValidator = () => {
  const validateImage = (file: File, key: string): Promise<ImageValidationResult> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const isPng = file.type === "image/png";
          const isSizeValid = file.size < 50000;
          const isDimensionsValid =
            key === "clinic_details_page" || key === "details_category"
              ? img.width === 360 && img.height === 200
              : img.width === 328 && img.height === 160;
          resolve({
            isValid: isPng && isSizeValid && isDimensionsValid,
            isPng,
            isSizeValid,
            isDimensionsValid,
            imgSrc: img.src,
          });
        };
      };
      reader.readAsDataURL(file);
    });
  };
  return { validateImage };
};

export const createServicePayload = (data: any) => {
  return data?.services?.map((service: any) => ({
    service_subservice_details: {
      service_id: service.service_id,
      sub_services_data: service.sub_services_data.map((s: any) => ({
        sub_service_id: s.sub_service_id,
      })),
    },
  }));
};

export const getEmptyClinicServices = (data: any) => {
  return data?.services?.filter((s: any) => !s?.sub_services_data?.length)?.map((s: any) => s?.service_id) ?? [];
};

export const validateClinicForm = (data: any, form: any) => {
  let isValid = true;
  if (!data?.services?.length) {
    form.setError("services", {
      type: "required",
      message: "Please select service and sub-service",
    });
    isValid = false;
  }
  const empty = getEmptyClinicServices(data);
  if (empty.length > 0) {
    form.setError("services", {
      type: "custom",
      message: "Sub-services should be selected",
    });
    isValid = false;
  }
  if (isValid) form.clearErrors("services");
  return isValid;
};

export const buildClinicPayload = (data: any, form: any, counterState: any) => {
  return {
    clinic_name: data?.clinicName,
    alias_code: data?.clinicCode,
    mobile: data?.mobile,
    email: data?.email,
    address: data?.address1,
    address2: data?.address2,
    nearby_landmarks: data?.nearby_landmarks,
    city_detail: {
      city_id: data?.city_select?.id,
      city_name: data?.city_select?.name,
    },
    state_name: data?.state,
    pincode: data?.pincode,
    latitude: data?.latitude,
    longitude: data?.longitude,
    clinic_cloning_id: data?.clinic_cloning_id,
    gstin: data?.gstn,
    service_details: createServicePayload(data),
    operational_capacity: counterState
      ?.filter((c: any) => c?.count)
      .map((s: any) => ({
        equipment_id: s.equipment_id,
        equipment_name: s.equipment_name,
        count: s.count.toString(),
      })),
    rating: data?.rating,
    total_reviews: data?.reviews,
    vetbuddy_instance_id: data?.instanceId,
    vetbuddy_clinic_id: data?.clinicId,
    vetbuddy_clinic_name: data?.vetbuddyClinicName,
    display_name: data?.displayName,
    review_url: data?.reviewUrl,
    gmb_place_id: data?.gmbPlaceId,
    location_url: data?.locationUrl,
    web_url_path: data?.webUrlPath,
    search_tags: data?.search_tags || [],
    visiting_hours: {
      monday: form.watch("monday"),
      tuesday: form.watch("tuesday"),
      wednesday: form.watch("wednesday"),
      thursday: form.watch("thursday"),
      friday: form.watch("friday"),
      saturday: form.watch("saturday"),
      sunday: form.watch("sunday"),
    },
    display_on_app: data?.displayOnApp || false,
    display_on_vms: data?.displayOnVms || false,
  };
};

export const useClinicImagesHydration = (
  clinicImagesList: any,
  formHook: any,
  setListcategories: any,
  setDetailcategories: any,
) => {
  useEffect(() => {
    if (!clinicImagesList?.length) return;
    const data = clinicImagesList[0];
    if (data?.image_url) {
      formHook.setValue("image", data.image_url.split(",")[0]);
    }
    // const hydrateCategories = (pageData: any, formKey: string, setter: any) => {
    //   if (!pageData) return;
    //   const { RECEPTION, FACADE, GROOMING, MEDICAL, RETAIL, Reception, Facade, Grooming, Medical, Retail, ...rest } =
    //     pageData;
    //   formHook.setValue(formKey, {
    //     RECEPTION: RECEPTION || Reception || [],
    //     FACADE: FACADE || Facade || [],
    //     GROOMING: GROOMING || Grooming || [],
    //     MEDICAL: MEDICAL || Medical || [],
    //     RETAIL: RETAIL || Retail || [],
    //   });
    //   setter((prev: any) => ({ ...prev, ...rest }));
    // };
    hydrateImageGroup(data?.clinic_listing_page, "clinic_listing_page", formHook, setListcategories);
    hydrateImageGroup(data?.clinic_details_page, "clinic_details_page", formHook, setDetailcategories);
  }, [clinicImagesList]);
};

export const getDefaultServiceIds = (clinicDetails: any, map: any) => {
  if (!clinicDetails?.services_data) return [];
  const parentIds =
    clinicDetails.services_data?.map((s: any) => map?.[s.service_id] ?? s.service_id)?.filter(Boolean) ?? [];
  const childIds =
    clinicDetails.services_data?.map((s: any) => s.sub_services_data?.map((ss: any) => ss.sub_service_id))?.flat() ??
    [];
  return [...childIds, ...parentIds];
};

export const hydrateImageGroup = (data: any, formKey: string, formHook: any, setter: any) => {
  if (!data) return;
  // const { RECEPTION, FACADE, GROOMING, MEDICAL, RETAIL, Reception, Facade, Grooming, Medical, Retail, ...rest } = data;
  // formHook.setValue(formKey, {
  //   RECEPTION: RECEPTION || Reception || [],
  //   FACADE: FACADE || Facade || [],
  //   GROOMING: GROOMING || Grooming || [],
  //   MEDICAL: MEDICAL || Medical || [],
  //   RETAIL: RETAIL || Retail || [],
  // });
  const {
    ENTRANCE,
    GROOMING,
    MEDICAL,
    "HAPPY PETS": HAPPY_PETS,
    Entrance,
    Grooming,
    Medical,
    "Happy Pets": HappyPets,
    ...rest
  } = data;
  formHook.setValue(formKey, {
    ENTRANCE: ENTRANCE || Entrance || [],
    GROOMING: GROOMING || Grooming || [],
    MEDICAL: MEDICAL || Medical || [],
    "HAPPY PETS": HAPPY_PETS || HappyPets || [],
  });
  setter((prev: any) => ({ ...prev, ...rest }));
};

export const useClinicFormHydration = (
  clinicDetails: any,
  formHook: any,
  setListcategories: any,
  setDetailcategories: any,
  setDefaultSelectedIds: any,
  serviceMap: any,
  days: string[],
) => {
  useEffect(() => {
    if (!clinicDetails) return;
    const clinicData = clinicDetails?.clinic_data;
    setDefaultSelectedIds(getDefaultServiceIds(clinicDetails, serviceMap));
    const fields: any = {
      clinicName: clinicData?.name,
      clinicCode: clinicData?.alias_code,
      mobile: clinicData?.mobile,
      email: clinicData?.email,
      address1: clinicData?.address,
      address2: clinicData?.address2 || "",
      nearby_landmarks: clinicData?.nearby_landmarks || "",
      city_select: clinicData?.city_details,
      state: clinicData?.state,
      pincode: clinicData?.pincode,
      latitude: `${clinicData?.latitude}`,
      longitude: `${clinicData?.longitude}`,
      gstn: clinicData?.gstin || "",
      rating: `${clinicData?.rating}`,
      reviews: `${clinicData?.total_ratings}`,
      instanceId: clinicData?.vetbuddy_instance_id,
      clinicId: clinicData?.vetbuddy_clinic_id,
      vetbuddyClinicName: clinicData?.vetbuddy_clinic_name,
      displayName: clinicData?.display_name,
      reviewUrl: clinicData?.review_url,
      gmbPlaceId: clinicData?.gmb_place_id,
      locationUrl: clinicData?.location_url,
      webUrlPath: clinicData?.clinic_cloning_id,
      search_tags: clinicData?.search_tags || [],
      displayOnApp: clinicData?.display_on_app,
      displayOnVms: clinicData?.display_on_vms,
    };
    Object.entries(fields).forEach(([key, value]) => {
      formHook.setValue(key, value);
    });
    if (clinicData?.image_url) {
      formHook.setValue("image", clinicData.image_url);
    }
    hydrateImageGroup(
      clinicData?.image_group_url_detail_v2?.clinic_listing_page,
      "clinic_listing_page",
      formHook,
      setListcategories,
    );
    hydrateImageGroup(
      clinicData?.image_group_url_list_v2?.clinic_details_page,
      "clinic_details_page",
      formHook,
      setDetailcategories,
    );
    days.forEach((day) => {
      const schedule = clinicDetails?.visiting_hours?.[day.toLowerCase()];
      if (schedule) formHook.setValue(day.toLowerCase(), schedule);
    });
  }, [clinicDetails, serviceMap]);
};

export const getGroomerColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Employee ID",
    accessor: "employee_id",
  },
  {
    header: "City",
    accessor: "city_name",
  },
  {
    header: "Service Type",
    accessor: "groomer_type",
  },
  {
    header: "Status",
    cell: (row: any) => {
      const isActive = row?.status?.toLowerCase() === "active";

      return React.createElement(
        "div",
        {
          className: `inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
            isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`,
        },
        React.createElement("span", {
          className: `w-2 h-2 rounded-full ${isActive ? "bg-green-500" : "bg-red-500"}`,
        }),
        row?.status,
      );
    },
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "GROOMER_EDIT_ACTION" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("GROOMER_SETUP_ONBOARD", "url")}?groomer_id=${row?.groomer_id}`);
            },
          },
          React.createElement(BiEdit, {
            className:
              "h-4 w-4 bg-slate-100 text-[#1F1F23] transition hover:bg-slate-200 rounded-xl items-center justify-center",
          }),
        ),
      ),
  },
];

export const getGroomerClinicColumns = (openModal: (clinic: any) => void) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Clinic",
    accessor: "clinic_name",
  },
  {
    header: "Groomers",
    accessor: "active_groomer_count",
  },
  {
    header: "Action",
    cell: (clinic: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "GROOMER_CLINIC_MAP" },
        React.createElement(
          "button",
          {
            type: "button",
            className:
              "inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 text-slate-700 transition hover:bg-slate-200",
            onClick: () => openModal(clinic),
          },
          React.createElement(BiEdit, {
            className: "h-4 w-4",
          }),
          "Map Groomer",
        ),
      ),
  },
];

export const getParavetColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Employee ID",
    accessor: "employee_id",
  },
  {
    header: "Phone Number",
    accessor: "phone_number",
  },
  {
    header: "City",
    accessor: "city_name",
  },
  {
    header: "Status",
    cell: (row: any) => {
      const isActive = row?.status?.toLowerCase() === "active";

      return React.createElement(
        "div",
        {
          className: `inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
            isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`,
        },
        React.createElement("span", {
          className: `w-2 h-2 rounded-full ${isActive ? "bg-green-500" : "bg-red-500"}`,
        }),
        row?.status,
      );
    },
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "PARAVET_EDIT_ACTION" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("PARAVET_SETUP_ONBOARD", "url")}?paravet_id=${row?.paravet_id}`);
            },
          },
          React.createElement(BiEdit, {
            className:
              "h-4 w-4 bg-slate-100 text-[#1F1F23] transition hover:bg-slate-200 rounded-xl items-center justify-center",
          }),
        ),
      ),
  },
];

export const getStaffProfileColumns = (onEdit: (staffId: string) => void) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Employee ID",
    accessor: "employee_id",
  },
  {
    header: "Type",
    cell: (row: any) => [row?.staff_type, row?.sub_type].filter(Boolean).join(" / "),
  },
  {
    header: "Phone",
    accessor: "phone_number",
  },
  {
    header: "Cities",
    cell: (row: any) => (row?.cities || []).map((city: any) => city?.city_name).join(", ") || "-",
  },
  {
    header: "Clinics",
    cell: (row: any) => (row?.clinic_ids || []).length,
  },
  {
    header: "Status",
    cell: (row: any) => {
      const isActive = row?.status?.toUpperCase() === "ACTIVE";
      return React.createElement(
        "div",
        {
          className: `w-fit rounded-full px-2 py-1 text-[11px] ${
            isActive ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600"
          }`,
        },
        row?.status,
      );
    },
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        "a",
        {
          href: "#",
          className: "text-indigo-600 hover:text-indigo-900",
          onClick: (e: any) => {
            e.preventDefault();
            onEdit(row?.staff_id);
          },
        },
        React.createElement(BiEdit, {
          className:
            "h-4 w-4 bg-slate-100 text-[#1F1F23] transition hover:bg-slate-200 rounded-xl items-center justify-center",
        }),
      ),
  },
];


export const getParavetClinicColumns = (openModal: (clinic: any) => void) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Clinic",
    accessor: "clinic_name",
  },
  {
    header: "Paravets",
    accessor: "active_paravet_count",
  },
  {
    header: "Action",
    cell: (clinic: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "PARAVET_CLINIC_MAP" },
        React.createElement(
          "button",
          {
            type: "button",
            className:
              "inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 text-slate-700 transition hover:bg-slate-200",
            onClick: () => openModal(clinic),
          },
          React.createElement(BiEdit, {
            className: "h-4 w-4",
          }),
          "Map Paravet",
        ),
      ),
  },
];

export const getStandardShiftsColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
  setActiveStat: any,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Shift Name",
    cell: (row: any) => startCase(row?.shift_name),
  },
  {
    header: "Shift Timing",
    cell: (row: any) => {
      return row?.shift_timings?.map((shift: any) => `${shift?.start_time} - ${shift?.end_time}`).join(", ");
    },
  },
  {
    header: "Shift Status",
    cell: (row: any) =>
      React.createElement(ShiftActiveDeactiveModalComponent, {
        flage: row?.shift_status,
        shift_id: row?.id,
        setActiveStat: setActiveStat,
      }),
  },
  {
    header: "Shift Edit",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "SHIFT_EDIT" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`${getRouteKey("SHIFT_ADD", "url")}?shift_id=${row?.id}`);
            },
          },
          "Edit",
        ),
      ),
  },
];

export const getAppointmentCartItemsMappingColumns = (
  navigate: (path: string) => void,
  getRouteKey: (module: string, key: string) => string,
) => [
  {
    header: "SN",
    cell: (_: any, index: number) => index + 1,
  },
  {
    header: "Sub Service Name",
    accessor: "sub_service_name",
  },
  {
    header: "Items",
    cell: (row: any) =>
      React.createElement(ExpandableList, {
        items: row.sub_services_map,
        getKey: (item: any) => item.sub_service_id,
        renderItem: (item: any) => item.sub_service_name,
      }),
  },
  {
    header: "Action",
    cell: (row: any) =>
      React.createElement(
        RBACFlag,
        { actionId: "APPOINTMENT_CART_ITEM_MAPPING_EDIT" },
        React.createElement(
          "a",
          {
            href: "#",
            className: "text-indigo-600 hover:text-indigo-900",
            onClick: (e: any) => {
              e.preventDefault();
              navigate(
                `${getRouteKey("APPOINTMENT_CART_ITEM_MAPPING_ONBOARD", "url")}?appointment_cart_item_mapping_id=${row?.sub_service_id}`,
              );
            },
          },
          "Edit",
        ),
      ),
  },
];
